import React, { useState, useEffect } from 'react';
import api, { booksAPI, departmentsAPI, categoriesAPI } from '../services/api';
import { getTodayInIsrael } from '../utils/dateUtils';
import BookModal from '../components/BookModal';
import AddBookModal from '../components/AddBookModal';
import ImportDuplicatesModal from '../components/ImportDuplicatesModal';

function BooksPage() {
  const [books, setBooks] = useState([]); // All books from first load
  const [displayBooks, setDisplayBooks] = useState([]); // Books to display on current page
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    limit: 100
  });
  const [selectedBook, setSelectedBook] = useState(null);
  const [showBookModal, setShowBookModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [departments, setDepartments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filters, setFilters] = useState({
    bookName: '',
    departmentId: '',
    categoryId: ''
  });
  
  // Import duplicates state
  const [importDuplicates, setImportDuplicates] = useState([]);
  const [showImportDuplicatesModal, setShowImportDuplicatesModal] = useState(false);

  // Load all books on initial page load
  useEffect(() => {
    loadAllBooks();
    loadDepartmentsAndCategories();
  }, []);

  // Apply search/filter when books are loaded
  useEffect(() => {
    applyFiltersAndPagination();
  }, [books]);

  // Apply search/filter when search term or filters change
  useEffect(() => {
    applyFiltersAndPagination();
  }, [searchTerm, filters.bookName, filters.departmentId, filters.categoryId, pagination.currentPage]);

  const loadDepartmentsAndCategories = async () => {
    try {
      const [deptsResponse, catsResponse] = await Promise.all([
        departmentsAPI.getAll(),
        categoriesAPI.getAll()
      ]);
      setDepartments(deptsResponse.data || []);
      setCategories(catsResponse.data || []);
    } catch (error) {
      console.error('Error loading departments/categories:', error);
    }
  };

  // Update available categories when department filter changes
  const handleDepartmentChange = async (deptId) => {
    setFilters(prev => ({ ...prev, departmentId: deptId })); // לא מאפסים את categoryId
    
    if (deptId) {
      try {
        const response = await booksAPI.getCategoriesByDepartment(deptId);
        setCategories(response.data || []);
      } catch (error) {
        console.error('Error loading categories for department:', error);
      }
    } else {
      // If no department selected, load all categories
      loadDepartmentsAndCategories();
    }
  };

  // Update available departments when category filter changes
  const handleCategoryChange = async (catId) => {
    setFilters(prev => ({ ...prev, categoryId: catId })); // לא מאפסים את departmentId
    
    if (catId) {
      try {
        const response = await booksAPI.getDepartmentsByCategory(catId);
        setDepartments(response.data || []);
      } catch (error) {
        console.error('Error loading departments for category:', error);
      }
    } else {
      // If no category selected, load all departments
      loadDepartmentsAndCategories();
    }
  };

  const loadAllBooks = async () => {
    try {
      setLoading(true);
      // Load ALL books at once (high limit)
      const response = await api.get('/books?page=1&limit=999999');
      
      let allBooks = [];
      if (response.data && response.data.data) {
        allBooks = response.data.data;
      } else if (Array.isArray(response.data)) {
        allBooks = response.data;
      }
      
      // Filter out null/invalid books
      const validBooks = allBooks.filter(book => book && book.id);
      setBooks(validBooks);
      
      // Reset to page 1 and apply filters
      setPagination(prev => ({ ...prev, currentPage: 1 }));
    } catch (error) {
      console.error('Error loading all books:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndPagination = () => {
    // Filter books based on search term and filter settings
    const filteredBooks = books.filter(book => {
      // Search term filtering (across name, author, barcode, department)
      const matchesSearch = !searchTerm.trim() || 
        (book.book_name || book.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (book.author || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (book.barcode || '').includes(searchTerm) ||
        (book.department_name || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      // Department filter
      const matchesDepartment = !filters.departmentId || book.department_id?.toString() === filters.departmentId;
      
      // Category filter
      const matchesCategory = !filters.categoryId || book.category_id?.toString() === filters.categoryId;
      
      // Book name filter
      const matchesBookName = !filters.bookName || (book.book_name || book.title || '').toLowerCase().includes(filters.bookName.toLowerCase());
      
      return matchesSearch && matchesDepartment && matchesCategory && matchesBookName;
    });

    // Calculate pagination
    const totalItems = filteredBooks.length;
    const limit = pagination.limit;
    const totalPages = Math.ceil(totalItems / limit) || 1;
    const currentPage = Math.min(pagination.currentPage, totalPages);
    
    // Get books for current page
    const offset = (currentPage - 1) * limit;
    const paginatedBooks = filteredBooks.slice(offset, offset + limit);
    
    setDisplayBooks(paginatedBooks);
    setPagination(prev => ({
      ...prev,
      currentPage,
      totalPages,
      totalItems
    }));
  };

  const handleSelectBook = async (book) => {
    try {
      const response = await booksAPI.getById(book.id);
      setSelectedBook(response.data);
      setShowBookModal(true);
    } catch (error) {
      console.error('Error loading book details:', error);
    }
  };

  const handleDeleteBook = async (id) => {
    if (window.confirm('האם אתה בטוח שברצונך למחוק את הספר הזה?')) {
      try {
        await booksAPI.delete(id);
        loadAllBooks(); // Reload all books after delete
        setShowBookModal(false);
      } catch (error) {
        console.error('Error deleting book:', error);
        alert('שגיאה במחיקת הספר');
      }
    }
  };

  const handleExport = async () => {
    try {
      // Only send filters that have values
      const exportFilters = {};
      if (filters.bookName) exportFilters.bookName = filters.bookName;
      if (filters.departmentId) exportFilters.departmentId = filters.departmentId;
      if (filters.categoryId) exportFilters.categoryId = filters.categoryId;
      
      const response = await booksAPI.export(exportFilters);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `books_${getTodayInIsrael()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      console.error('Error exporting books:', error);
      alert('שגיאה בייצוא הנתונים');
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        setLoading(true);
        const response = await booksAPI.import(file);
        
        if (response.data.duplicates && response.data.duplicates.length > 0) {
          setImportDuplicates(response.data.duplicates);
          setShowImportDuplicatesModal(true);
          
          // If there were also successful imports, show a message
          if (response.data.totalBooks > 0) {
            alert(`${response.data.totalBooks} ספרים יובאו בהצלחה. נמצאו ${response.data.duplicates.length} כפילויות.`);
          }
        } else {
          alert('הספרים יובאו בהצלחה');
        }
        
        loadAllBooks(); // Reload all books after import
      } catch (error) {
        console.error('Error importing books:', error);
        alert('שגיאה בייבוא הספרים');
      } finally {
        setLoading(false);
        // Reset file input
        event.target.value = '';
      }
    }
  };

  const handleImportDuplicatesConfirm = async (booksToAdd) => {
    try {
      setLoading(true);
      await booksAPI.processImportDuplicates(booksToAdd);
      alert('הספרים הנוספים יובאו בהצלחה');
      setShowImportDuplicatesModal(false);
      setImportDuplicates([]);
      loadAllBooks(); // Reload all books after processing duplicates
    } catch (error) {
      console.error('Error processing duplicates:', error);
      alert('שגיאה בעיבוד הספרים הכפולים');
    } finally {
      setLoading(false);
    }
  };

  const filteredBooks = displayBooks;

  if (loading) {
    return <div className="loading">טוען ספרים...</div>;
  }

  return (
    <div className="books-page">
      <h1 className="page-title">📖 ניהול ספרים</h1>

      <div className="top-bar">
        <div className="search-box">
          <input
            type="text"
            placeholder="חפש לפי שם ספר או מחבר..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="action-buttons">
          <div className="add-book-wrapper">
            <button className="add-book-btn" onClick={() => setShowAddMenu(!showAddMenu)}>
              ➕ הוסף ספר
            </button>
            {showAddMenu && (
              <div className="add-menu-dropdown">
                <button className="menu-item" onClick={() => { setShowAddModal(true); setShowAddMenu(false); }}>
                  <span className="menu-icon">📝</span> הוסף ספר חדש
                </button>
                <label className="menu-item" style={{ cursor: 'pointer', border: 'none' }}>
                  <span className="menu-icon">📊</span> יבוא מאקסל
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleImport}
                    style={{ display: 'none' }}
                  />
                </label>
              </div>
            )}
          </div>
          <button className="btn btn-warning" onClick={handleExport}>
            📊 ייצוא לאקסל
          </button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ background: 'white', padding: '15px', borderRadius: '8px', marginBottom: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <h3 style={{ marginBottom: '15px' }}>סינון וייצוא</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
          <div className="form-group">
            <label>שם ספר:</label>
            <input
              type="text"
              placeholder="חפש בשם..."
              value={filters.bookName}
              onChange={(e) => setFilters(prev => ({ ...prev, bookName: e.target.value }))}
            />
          </div>
          <div className="form-group">
            <label>מחלקה:</label>
            <select 
              value={filters.departmentId} 
              onChange={(e) => handleDepartmentChange(e.target.value)}
            >
              <option value="">הכל</option>
              {departments.map(dept => (
                <option key={dept.id} value={dept.id}>{dept.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>קטגוריה:</label>
            <select 
              value={filters.categoryId} 
              onChange={(e) => handleCategoryChange(e.target.value)}
            >
              <option value="">הכל</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>
        </div>
        <div style={{ marginTop: '10px', fontSize: '0.9rem', color: '#666', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>📊 תוצאות: {filteredBooks.length} ספרים בדף זה (מתוך {pagination.totalItems} סה"כ)</div>

          {pagination.totalPages > 1 && (
            <div className="pagination-controls" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <button
                className="btn btn-secondary btn-sm"
                disabled={pagination.currentPage === 1}
                onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
              >
                ◀ הקודם
              </button>
              <span>דף {pagination.currentPage} מתוך {pagination.totalPages}</span>
              <button
                className="btn btn-secondary btn-sm"
                disabled={pagination.currentPage === pagination.totalPages}
                onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
              >
                הבא ▶
              </button>
            </div>
          )}
        </div>
      </div>

      {filteredBooks.length === 0 ? (
        <div className="empty-state">
          <h3>אין ספרים</h3>
          <p>התחל בהוספת ספרים לספריה</p>
        </div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>ברקוד</th>
              <th>שם הספר</th>
              <th>מחבר</th>
              <th>מחלקה</th>
              <th>קטגוריה</th>
              <th>זמינות</th>
              <th>פעולות</th>
            </tr>
          </thead>
          <tbody>
            {filteredBooks.map((book) => (
              <tr key={book.id} onClick={() => handleSelectBook(book)} style={{ cursor: 'pointer' }}>
                <td>{book.barcode || '-'}</td>
                <td>{book.book_name || book.title}</td>
                <td>{book.author}</td>
                <td>{book.department_name || '-'}</td>
                <td>{book.category_name || '-'}</td>
                <td>
                  <span className={`badge badge-${book.status_text?.includes('צפוי להיות זמין') ? 'borrowed' : book.status_text?.includes('זמין') ? 'available' : 'borrowed'}`}>
                    {book.status_text || 'זמין'}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteBook(book.id);
                      }}
                    >
                      🗑️ מחק
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showAddModal && (
        <AddBookModal
          onClose={() => setShowAddModal(false)}
          onBookAdded={() => {
            loadAllBooks();
            setShowAddModal(false);
          }}
        />
      )}

      {showBookModal && selectedBook && (
        <BookModal
          book={selectedBook.book}
          notes={selectedBook.notes}
          loans={selectedBook.loans}
          onClose={() => setShowBookModal(false)}
          onUpdate={() => {
            // Re-fetch the updated book data with latest loans
            handleSelectBook(selectedBook.book);
          }}
          onDeleted={() => {
            loadAllBooks();
            setShowBookModal(false);
          }}
        />
      )}

      {showImportDuplicatesModal && (
        <ImportDuplicatesModal
          duplicates={importDuplicates}
          onClose={() => setShowImportDuplicatesModal(false)}
          onConfirm={handleImportDuplicatesConfirm}
        />
      )}
    </div>
  );
}

export default BooksPage;
